import { useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { DeskPcSpec } from "../scenario/types";

const DEG = Math.PI / 180;

/** Screen-space pin (percent of viewport) for the clickable sticky note. */
export type DeskPin = { xPct: number; yPct: number; visible: boolean; distance: number };

/** A muted "locked" desktop drawn on the monitor mesh (system fonts, CSP-safe). */
function makeScreenTexture(title: string, user: string): THREE.CanvasTexture {
  const w = 1024;
  const h = 640;
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const g = cv.getContext("2d")!;
  const grad = g.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, "#1b2b46");
  grad.addColorStop(1, "#0c1526");
  g.fillStyle = grad;
  g.fillRect(0, 0, w, h);
  // Lock avatar
  g.fillStyle = "rgba(255,255,255,0.12)";
  g.beginPath();
  g.arc(w / 2, h * 0.34, 64, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = "rgba(255,255,255,0.85)";
  g.font = "600 30px Segoe UI, Arial, sans-serif";
  g.textAlign = "center";
  g.fillText(user, w / 2, h * 0.52);
  g.fillStyle = "rgba(255,255,255,0.45)";
  g.font = "18px Segoe UI, Arial, sans-serif";
  g.fillText("Enter password", w / 2, h * 0.6);
  // password field
  g.fillStyle = "rgba(255,255,255,0.14)";
  g.fillRect(w / 2 - 150, h * 0.64, 300, 44);
  g.fillStyle = "rgba(255,255,255,0.3)";
  g.font = "22px Segoe UI, Arial, sans-serif";
  g.fillText("•••••••••", w / 2, h * 0.685);
  g.fillStyle = "rgba(255,255,255,0.5)";
  g.font = "16px Segoe UI, Arial, sans-serif";
  g.fillText(title, w / 2, h * 0.9);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/** The yellow sticky note (handwritten-ish password) drawn as a texture. */
function makeStickyTexture(caption: string, password: string): THREE.CanvasTexture {
  const s = 512;
  const cv = document.createElement("canvas");
  cv.width = s;
  cv.height = s;
  const g = cv.getContext("2d")!;
  g.fillStyle = "#ffe873";
  g.fillRect(0, 0, s, s);
  g.fillStyle = "rgba(0,0,0,0.06)";
  g.fillRect(0, 0, s, 40);
  g.fillStyle = "#4a3a10";
  g.textAlign = "center";
  g.font = "500 40px Comic Sans MS, Segoe Script, cursive";
  g.fillText(caption, s / 2, s * 0.38);
  g.fillStyle = "#161616";
  g.font = "700 58px Comic Sans MS, Segoe Script, cursive";
  g.fillText(password, s / 2, s * 0.6);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/**
 * A procedural PC workstation (monitor + stand + keyboard + a yellow sticky
 * note on the bezel) placed on a desk in the walkable glTF room. Each frame it
 * projects the sticky note's world position to screen % and reports it up via
 * `onPin`, so App can render a clickable DOM hotspot glued to it.
 */
export function DeskWorkstation({
  spec,
  onPin,
}: {
  spec: DeskPcSpec;
  onPin: (pin: DeskPin) => void;
}) {
  const { camera } = useThree();
  const group = useRef<THREE.Group>(null);
  const stickyAnchor = useRef<THREE.Group>(null);
  const worldPos = useRef(new THREE.Vector3()).current;
  const camDir = useRef(new THREE.Vector3()).current;
  const toPin = useRef(new THREE.Vector3()).current;
  const last = useRef<DeskPin>({ xPct: 0, yPct: 0, visible: false, distance: 999 });
  const onPinRef = useRef(onPin);
  onPinRef.current = onPin;

  const scale = spec.scale ?? 1;
  const yaw = (spec.rotationDeg ?? 0) * DEG;

  const screenTex = useMemo(
    () => makeScreenTexture(spec.screenTitle ?? "NorthBay Financial", spec.userName ?? "o.deutsch"),
    [spec.screenTitle, spec.userName],
  );
  const stickyTex = useMemo(
    () => makeStickyTexture(spec.stickyCaption ?? "portal login", spec.password ?? "Summer2024!"),
    [spec.stickyCaption, spec.password],
  );

  useFrame(() => {
    const anchor = stickyAnchor.current;
    if (!anchor) return;
    anchor.getWorldPosition(worldPos);
    camera.getWorldDirection(camDir);
    toPin.copy(worldPos).sub(camera.position);
    const distance = toPin.length();
    const inFront = toPin.normalize().dot(camDir) > 0.1;
    const proj = worldPos.clone().project(camera);
    const xPct = (proj.x * 0.5 + 0.5) * 100;
    const yPct = (-proj.y * 0.5 + 0.5) * 100;
    const onScreen = xPct > -5 && xPct < 105 && yPct > -5 && yPct < 105;
    const pin: DeskPin = {
      xPct,
      yPct,
      visible: inFront && onScreen && distance < 6,
      distance,
    };
    const p = last.current;
    if (
      p.visible !== pin.visible ||
      Math.abs(p.xPct - pin.xPct) > 0.3 ||
      Math.abs(p.yPct - pin.yPct) > 0.3 ||
      Math.abs(p.distance - pin.distance) > 0.05
    ) {
      last.current = pin;
      onPinRef.current(pin);
    }
    if (typeof window !== "undefined") {
      (window as unknown as { __deskPin?: DeskPin }).__deskPin = pin;
    }
  });

  // The whole workstation is FLOOR-anchored: `spec.position` is a point on the
  // floor; the desk top sits at DESK_H so the PC always rests on its own surface
  // (no dependence on the purchased model's furniture heights). Screen + keyboard
  // face +Z locally, so rotationDeg 180 faces a learner approaching along +Z.
  const DESK_H = 0.74;
  return (
    <group ref={group} position={spec.position} rotation={[0, yaw, 0]} scale={scale}>
      {/* Desk top */}
      <mesh position={[0, DESK_H, 0]}>
        <boxGeometry args={[1.5, 0.05, 0.72]} />
        <meshStandardMaterial color="#e8e2d6" metalness={0.05} roughness={0.55} />
      </mesh>
      {/* Desk legs */}
      {([
        [-0.7, 0.31],
        [0.7, 0.31],
        [-0.7, -0.31],
        [0.7, -0.31],
      ] as [number, number][]).map(([lx, lz], i) => (
        <mesh key={i} position={[lx, DESK_H / 2, lz]}>
          <boxGeometry args={[0.05, DESK_H, 0.05]} />
          <meshStandardMaterial color="#2a2f37" metalness={0.5} roughness={0.4} />
        </mesh>
      ))}

      {/* Monitor stand */}
      <mesh position={[0, DESK_H + 0.05, -0.05]}>
        <cylinderGeometry args={[0.11, 0.13, 0.02, 24]} />
        <meshStandardMaterial color="#20242b" metalness={0.6} roughness={0.35} />
      </mesh>
      <mesh position={[0, DESK_H + 0.17, -0.07]}>
        <boxGeometry args={[0.04, 0.24, 0.04]} />
        <meshStandardMaterial color="#20242b" metalness={0.6} roughness={0.35} />
      </mesh>
      {/* Monitor body */}
      <group position={[0, DESK_H + 0.46, -0.07]}>
        <mesh>
          <boxGeometry args={[0.62, 0.38, 0.03]} />
          <meshStandardMaterial color="#111418" metalness={0.4} roughness={0.5} />
        </mesh>
        {/* Screen (emissive so it reads as "on" even in soft light) */}
        <mesh position={[0, 0, 0.018]}>
          <planeGeometry args={[0.58, 0.34]} />
          <meshStandardMaterial
            map={screenTex}
            emissive="#ffffff"
            emissiveMap={screenTex}
            emissiveIntensity={0.55}
            toneMapped={false}
          />
        </mesh>
        {/* Sticky note stuck on the bottom-right CORNER of the screen, angled
            slightly. Raised from y=-0.24 (which sat low under the monitor and
            read as "hidden at the bottom") up to -0.15 so it overlaps the
            screen's bottom-right corner — far from the centred login fields,
            much more visible, and clearly attached to the monitor.
            Skipped entirely for `hideStickyNote` courses (guided courses
            teach "click the screen itself", not "find the note" — the note
            would otherwise still render even with its hotspot disabled). */}
        {!spec.hideStickyNote && (
          <group ref={stickyAnchor} position={[0.24, -0.15, 0.03]} rotation={[0, 0, -0.12]}>
            <mesh>
              <planeGeometry args={[0.14, 0.14]} />
              <meshStandardMaterial map={stickyTex} roughness={0.9} side={THREE.DoubleSide} />
            </mesh>
          </group>
        )}
      </group>
      {/* Keyboard */}
      <mesh position={[0, DESK_H + 0.04, 0.18]} rotation={[-0.04, 0, 0]}>
        <boxGeometry args={[0.44, 0.02, 0.15]} />
        <meshStandardMaterial color="#2a2f37" metalness={0.3} roughness={0.6} />
      </mesh>
      {/* Mouse */}
      <mesh position={[0.3, DESK_H + 0.04, 0.18]}>
        <boxGeometry args={[0.06, 0.02, 0.1]} />
        <meshStandardMaterial color="#2a2f37" metalness={0.3} roughness={0.6} />
      </mesh>
    </group>
  );
}
